import axios from 'axios';
import { baseURL } from '../../api/api';

export const TOKEN_KEY = 'token';
export const USER_KEY = 'user-info';

/**
 * Единая точка входа в API для админки.
 *
 * Раньше каждый компонент сам собирал baseURL + 'api/...' и вручную подставлял
 * заголовок Authorization, а ошибки уходили в console.log. Здесь это собрано в одном
 * месте: токен подставляется автоматически, 401 разлогинивает, а любая ошибка
 * приводится к предсказуемому виду ApiError.
 */
const client = axios.create({
    baseURL: baseURL + 'api/',
});

client.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
        config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
    }
    return config;
});

/** Куда отправлять при истёкшей сессии. Устанавливается из AuthProvider. */
let onUnauthorized = null;
export const setUnauthorizedHandler = (handler) => {
    onUnauthorized = handler;
};

export class ApiError extends Error {
    constructor(message, { status = 0, errors = null, code = null, payload = null } = {}) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.errors = errors;
        this.code = code;
        this.payload = payload;
    }

    /** Первое сообщение валидации для конкретного поля. */
    fieldError(field) {
        const list = this.errors && this.errors[field];
        return Array.isArray(list) ? list[0] : list || null;
    }

    get isValidation() {
        return this.status === 422;
    }

    /** 409 — запись используется в других разделах, удаление требует подтверждения. */
    get isConflict() {
        return this.status === 409;
    }
}

client.interceptors.response.use(
    (response) => response,
    (error) => {
        if (!error.response) {
            return Promise.reject(
                new ApiError(
                    'Сервер недоступен. Проверьте соединение и попробуйте ещё раз.',
                    { status: 0 }
                )
            );
        }

        const { status, data } = error.response;

        if (status === 401) {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            if (onUnauthorized) onUnauthorized();
            return Promise.reject(
                new ApiError('Сессия истекла. Войдите заново.', { status })
            );
        }

        const message =
            (data && data.message) ||
            (status === 404 ? 'Запись не найдена.' : 'Не удалось выполнить операцию.');

        return Promise.reject(
            new ApiError(message, {
                status,
                errors: data && data.errors,
                code: data && data.code,
                payload: data,
            })
        );
    }
);

/**
 * Собирает FormData: массивы разворачиваются в name[], File передаётся как есть,
 * булево — как 1/0 (PHP-валидатор boolean понимает именно такие значения),
 * null/undefined пропускаются, чтобы бэкенд отличал «не прислали» от «очистили».
 */
export const toFormData = (values) => {
    const form = new FormData();

    Object.entries(values).forEach(([key, value]) => {
        if (value === undefined || value === null) return;

        if (Array.isArray(value)) {
            if (value.length === 0) {
                // Пустой массив — это осознанная очистка связи.
                form.append(`${key}[]`, '');
            } else {
                value.forEach((v) => form.append(`${key}[]`, v));
            }
            return;
        }

        if (typeof value === 'boolean') {
            form.append(key, value ? '1' : '0');
            return;
        }

        form.append(key, value);
    });

    return form;
};

export default client;
